// Read-only events catalogue shared by employee + supervisor.
// Shows every current & future event grouped per (location, position).
// Compact card layout: only the must-know facts are visible by default; full
// detail page is reached via a "VIEW DETAILS" button. The collapsible
// "▼ More Information" panel surfaces optional notes inline for users who
// want a quick peek without leaving the list.
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  CalendarRange,
  Tent,
  Hotel,
  Bus,
  Search,
  StickyNote,
  PoundSterling,
  Shield,
  ChevronRight,
  ChevronDown,
  Shirt,
  Users,
  ArrowLeft,
  Briefcase,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try { return format(parseISO(iso), 'EEE dd MMM yyyy'); } catch { return iso; }
};
const formatDateShort = (iso) => {
  if (!iso) return '';
  try { return format(parseISO(iso), 'dd MMM yyyy'); } catch { return iso; }
};

// Spaces-left helper. We treat negative results as zero (defensive against
// data anomalies where claim_count > quantity due to legacy admin edits).
const spacesLeft = (ev) => {
  const cap = Number(ev.total_quantity) || 0;
  const taken = Number(ev.total_claimed) || 0;
  return Math.max(0, cap - taken);
};

export default function EventsListView({ title = 'Events List', basePath = '/employee/events-list' }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState(null);
  const [moreOpen, setMoreOpen] = useState({}); // per-group expand for "More Information"
  const location = useLocation();
  const navigate = useNavigate();

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/staff/events-list`);
      setEvents(res.data?.events || []);
    } catch (_e) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Sync the selected event with the URL hash so the back button works and
  // the details view can be deep-linked from elsewhere in the app.
  useEffect(() => {
    const m = location.hash && location.hash.startsWith('#event=')
      ? decodeURIComponent(location.hash.slice('#event='.length))
      : null;
    setSelectedKey(m);
  }, [location.hash]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter(e =>
      (e.location || '').toLowerCase().includes(q) ||
      (e.position || '').toLowerCase().includes(q) ||
      (e.notes || '').toLowerCase().includes(q)
    );
  }, [events, search]);

  const selected = useMemo(
    () => events.find(e => e.group_key === selectedKey) || null,
    [events, selectedKey],
  );

  // Detail view replaces the list when an event is selected. We keep the
  // routing single-page for speed — no extra page mount cost.
  if (selected) {
    return (
      <EventDetail
        ev={selected}
        onBack={() => navigate(basePath)}
      />
    );
  }

  const toggleMore = (key) =>
    setMoreOpen((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div className="p-4 sm:p-6 space-y-4 min-h-screen">
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
          <CalendarRange className="w-6 h-6 text-primary" strokeWidth={1.5} />
          {title.toUpperCase()}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          All scheduled events. Tap an event to see full details.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by location, position or notes…"
          className="pl-10 bg-background border-border rounded-sm"
          data-testid="events-list-search"
        />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground p-4">Loading events…</p>
      ) : filtered.length === 0 ? (
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-12 text-center space-y-2">
            <CalendarRange className="w-12 h-12 text-muted-foreground mx-auto opacity-50" />
            <p className="text-sm text-muted-foreground">
              {search ? 'No events match your search.' : 'No upcoming events scheduled.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filtered.map((ev) => (
            <EventCard
              key={ev.group_key}
              ev={ev}
              moreOpen={!!moreOpen[ev.group_key]}
              onToggleMore={() => toggleMore(ev.group_key)}
              onOpenDetail={() => navigate(`${basePath}#event=${encodeURIComponent(ev.group_key)}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Compact card ----
function EventCard({ ev, moreOpen, onToggleMore, onOpenDetail }) {
  const span = ev.start_date === ev.end_date
    ? formatDateShort(ev.start_date)
    : `${formatDateShort(ev.start_date)} → ${formatDateShort(ev.end_date)}`;
  const left = spacesLeft(ev);
  const isFull = ev.total_quantity > 0 && left === 0;
  const hasMore = !!(ev.transport_notes || ev.accommodation_notes || ev.notes);

  return (
    <Card
      className="bg-card border-border rounded-sm hover:border-primary/40 transition-colors"
      data-testid={`event-${ev.group_key}`}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header — most prominent: location + dates */}
        <div className="space-y-1">
          <h3 className="text-lg font-semibold leading-tight">
            {ev.location || 'Unnamed event'}
          </h3>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-sm text-primary">
              <CalendarRange className="w-4 h-4" />
              <span>{span}</span>
              {ev.days_count > 1 && (
                <span className="text-[10px] text-muted-foreground">({ev.days_count} days)</span>
              )}
            </div>
            <AvailabilityBadge spacesLeft={left} isFull={isFull} />
          </div>
        </div>

        {/* Highlight strip — position + hourly pay (the two facts staff
            care about most when scanning the list). Drops to a single
            column when only one of the two is available so the chip
            doesn't get awkwardly truncated. */}
        {(ev.position || ev.hourly_payment) && (
          <div className={`grid gap-2 ${(ev.position && ev.hourly_payment) ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {ev.position && (
              <div className="bg-muted/30 border border-border/60 rounded-sm px-2 py-1.5 flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-sm font-medium truncate" data-testid={`event-position-${ev.group_key}`}>
                  {ev.position}
                </span>
              </div>
            )}
            {ev.hourly_payment && (
              <div className="bg-green-500/5 border border-green-500/30 rounded-sm px-2 py-1.5 flex items-center gap-1.5">
                <PoundSterling className="w-3.5 h-3.5 text-green-400 shrink-0" />
                <span className="text-sm font-semibold text-green-300" data-testid={`event-pay-${ev.group_key}`}>
                  £{Number(ev.hourly_payment).toFixed(2)} / hr
                </span>
              </div>
            )}
          </div>
        )}

        {/* Quick amenity badges row */}
        <div className="flex flex-wrap gap-1.5">
          {ev.sia_required && (
            <Badge variant="outline" className="text-[10px] uppercase border-amber-500/30 text-amber-300 bg-amber-500/5">
              <Shield className="w-3 h-3 mr-1" />
              SIA: {ev.sia_required === 'door_supervisor' ? 'Door Supervisor' : ev.sia_required}
            </Badge>
          )}
          <ProvidedBadge label="Camping" provided={ev.camping_provided} icon={<Tent className="w-3 h-3" />} />
          <ProvidedBadge label="Accommodation" provided={ev.accommodation_provided} icon={<Hotel className="w-3 h-3" />} />
          <ProvidedBadge label="Transport" provided={ev.transport_provided} icon={<Bus className="w-3 h-3" />} />
        </div>

        {/* Collapsible More Information */}
        {hasMore && (
          <div>
            <button
              type="button"
              onClick={onToggleMore}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid={`event-more-toggle-${ev.group_key}`}
              aria-expanded={moreOpen}
            >
              {moreOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              More Information
            </button>
            {moreOpen && (
              <div className="mt-2 space-y-1.5 border-l-2 border-border/60 pl-3">
                {ev.transport_notes && (
                  <p className="text-xs text-muted-foreground"><strong>Transport:</strong> {ev.transport_notes}</p>
                )}
                {ev.accommodation_notes && (
                  <p className="text-xs text-muted-foreground"><strong>Accommodation:</strong> {ev.accommodation_notes}</p>
                )}
                {ev.notes && (
                  <p className="text-xs text-muted-foreground flex gap-1.5 items-start">
                    <StickyNote className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{ev.notes}</span>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <Button
          onClick={onOpenDetail}
          className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider font-semibold"
          data-testid={`event-view-${ev.group_key}`}
        >
          View Details
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
}

// ---- Availability indicator ----
function AvailabilityBadge({ spacesLeft, isFull }) {
  if (isFull) {
    return (
      <Badge className="bg-red-500/10 text-red-400 border-red-500/30 text-[10px] uppercase tracking-wider">
        Full
      </Badge>
    );
  }
  if (spacesLeft <= 0) {
    return null; // unknown capacity — don't mislead with "0 spaces"
  }
  const tone = spacesLeft <= 3
    ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
    : 'bg-green-500/10 text-green-400 border-green-500/30';
  return (
    <Badge className={`${tone} text-[10px] uppercase tracking-wider flex items-center gap-1`}>
      <Users className="w-3 h-3" />
      {spacesLeft} space{spacesLeft === 1 ? '' : 's'} left
    </Badge>
  );
}

// ---- Yes/No provided badge per spec colours ----
function ProvidedBadge({ label, provided, icon }) {
  if (provided === undefined || provided === null) return null;
  const cls = provided
    ? 'bg-green-500/10 text-green-400 border-green-500/30'
    : 'bg-red-500/10 text-red-400 border-red-500/30';
  return (
    <Badge variant="outline" className={`text-[10px] uppercase border ${cls} flex items-center gap-1`}>
      {icon}
      {label}: {provided ? 'Provided' : 'Not Provided'}
    </Badge>
  );
}

// ---- Full Details view ----
function EventDetail({ ev, onBack }) {
  const left = spacesLeft(ev);
  const isFull = ev.total_quantity > 0 && left === 0;
  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-3xl mx-auto">
      <Button
        variant="outline"
        onClick={onBack}
        className="border-border rounded-sm text-xs"
        data-testid="event-detail-back"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" />
        Back to Events
      </Button>

      <div className="space-y-2">
        <h1 className="text-2xl font-barlow tracking-widest">{ev.location || 'Unnamed event'}</h1>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-primary">
            <CalendarRange className="w-4 h-4" />
            <span>
              {ev.start_date === ev.end_date
                ? formatDate(ev.start_date)
                : `${formatDate(ev.start_date)} → ${formatDate(ev.end_date)}`}
            </span>
            {ev.days_count > 1 && (
              <span className="text-xs text-muted-foreground">({ev.days_count} days)</span>
            )}
          </div>
          <AvailabilityBadge spacesLeft={left} isFull={isFull} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {ev.position && (
          <DetailTile icon={<Briefcase className="w-4 h-4 text-primary" />} label="Position" value={ev.position} />
        )}
        {ev.hourly_payment && (
          <DetailTile icon={<PoundSterling className="w-4 h-4 text-green-400" />} label="Hourly pay" value={`£${Number(ev.hourly_payment).toFixed(2)} / hr`} highlight />
        )}
        {ev.dress_code && (
          <DetailTile icon={<Shirt className="w-4 h-4 text-blue-400" />} label="Dress code" value={ev.dress_code} />
        )}
        {ev.sia_required && (
          <DetailTile icon={<Shield className="w-4 h-4 text-amber-400" />} label="SIA required" value={ev.sia_required === 'door_supervisor' ? 'Door Supervisor' : ev.sia_required} />
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Logistics</p>
        <div className="flex flex-wrap gap-1.5">
          <ProvidedBadge label="Camping" provided={ev.camping_provided} icon={<Tent className="w-3 h-3" />} />
          <ProvidedBadge label="Accommodation" provided={ev.accommodation_provided} icon={<Hotel className="w-3 h-3" />} />
          <ProvidedBadge label="Transport" provided={ev.transport_provided} icon={<Bus className="w-3 h-3" />} />
        </div>
        {ev.accommodation_provided && ev.accommodation_cost_per_day && (
          <p className="text-xs text-muted-foreground">
            Accommodation cost: <span className="text-foreground">£{Number(ev.accommodation_cost_per_day).toFixed(2)} / day</span>
          </p>
        )}
        {ev.transport_provided && (ev.transport_location || ev.transport_departure_time) && (
          <p className="text-xs text-muted-foreground">
            Pick-up: <span className="text-foreground">{[ev.transport_location, ev.transport_departure_time].filter(Boolean).join(' · ')}</span>
          </p>
        )}
        {ev.transport_notes && (
          <p className="text-xs text-muted-foreground"><strong>Transport note:</strong> {ev.transport_notes}</p>
        )}
        {ev.accommodation_notes && (
          <p className="text-xs text-muted-foreground"><strong>Accommodation note:</strong> {ev.accommodation_notes}</p>
        )}
      </div>

      {ev.notes && (
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Notes</p>
          <p className="text-sm text-foreground/90 whitespace-pre-wrap">{ev.notes}</p>
        </div>
      )}

      <Card className="bg-primary/5 border-primary/30 rounded-sm">
        <CardContent className="p-4 text-sm text-primary">
          All information regarding this event will be posted in the dedicated WhatsApp group.
        </CardContent>
      </Card>
    </div>
  );
}

function DetailTile({ icon, label, value, highlight }) {
  return (
    <div className={`rounded-sm border px-3 py-2 ${highlight ? 'bg-green-500/5 border-green-500/30' : 'bg-card border-border'}`}>
      <div className="flex items-center gap-1.5">
        {icon}
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <p className={`text-sm mt-0.5 ${highlight ? 'text-green-300 font-semibold' : ''}`}>{value}</p>
    </div>
  );
}
